
import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useActionButtonStyles } from "@/hooks/useActionButtonStyles";
import { Heart, Bookmark } from "lucide-react";
import { ActionButtonProps } from "./types";

/**
 * UnifiedActionButton - A consistent button component for all favorite/watchlist toggles
 * Simplified to use the button variants directly from the button component
 */
const UnifiedActionButton: React.FC<ActionButtonProps> = ({
  intent,
  context,
  isActive,
  isDeselected = false,
  isHovered = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
  showLabel = true
}) => {
  // Use our simplified hook to get variant, size, and label text
  const { variant, size, labelText } = useActionButtonStyles(
    intent, 
    context, 
    isActive, 
    isDeselected
  );
  
  // Select the appropriate icon component based on intent
  const IconComponent = intent === "favorite" ? Heart : Bookmark;
  
  // Determine the fill color based on active state and deselected state
  const getFillColor = () => {
    // If deselected, show unfilled icon regardless of previous active state
    if (isDeselected) return "none";
    
    // If not active, show unfilled icon
    if (!isActive) return "none";
    
    // Return the appropriate fill color based on button context for active state
    if (context === "grid" || context === "dialog" || context === "detail") {
      return "#212529"; // Use the actual mono-eerieBlack color that matches grid buttons
    } else {
      return "#f8f9fa"; // Mono-seasalt color for list and search views
    }
  };
  
  return (
    <Button 
      variant={variant as any}
      size={size}
      className={cn(
        "group", // Removed transition classes to make fill/unfill instantaneous
        isHovered && "hover" // Add hover class if isHovered is true
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      title={labelText}
      type="button"
      deselected={isDeselected}
      hovered={isHovered}
    >
      <IconComponent 
        className="shrink-0" 
        fill={getFillColor()}
        strokeWidth={isActive && !isDeselected ? 1.5 : 2} // Slightly thinner stroke when filled and not deselected
      />
      {showLabel && <span className="truncate">{labelText}</span>}
    </Button>
  );
};

export default UnifiedActionButton;
